import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import {
  AssetType,
  CoverType,
  ICancelPolicyAsset,
  IClaim,
  IClaimCategories,
  IClaimNumber,
  IDealerships,
  IEmergencyNumber,
  IInsuredAsset,
  IInsurersDetails,
  IPolicyBenefit,
  ISchedule,
  IT2wof,
} from '@takectrl-multiapp/svcs-share-config';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ICtrlResponse } from '../interfaces/api.interface';
import { ApiCacheService } from './api-cache.service';
import { APIHttpService } from './api-http.service';

@Injectable({
  providedIn: 'root',
})
export class APIPolicyService {
  private readonly CACHE_KEYS = {
    EMERGENCY_NUMBERS: 'emergency_numbers',
    DEALERSHIPS: (params: string) => `dealerships_${params}`,
    INSURED_ASSET: (type: AssetType, id: number) => `insured_asset_${type}_${id}`,
    POLICY_BENEFIT: (type: AssetType, coverType: CoverType, insurerId: string) => `policy_benefit_${insurerId}_${type}_${coverType}`,
    VAPS_POLICY_BENEFIT: (insurerId: string, coverType: CoverType, productId: string) => `vaps_policy_${insurerId}_${coverType}_${productId}`,
    ALL_POLICY_BENEFITS: (coverType: CoverType) => `all_policy_benefits_${coverType}`,
    T2WOF: 't2wof',
    INSURERS: 'insurers',
    CLAIM_NUMBERS: 'claim_numbers',
    CLAIM_CATEGORIES: 'claim_categories',
  };

  constructor(
    private readonly ctrlHttp: APIHttpService,
    public firebase: AngularFireDatabase,
    private readonly ctrlApiHttpService: APIHttpService,
    private readonly cacheService: ApiCacheService
  ) {}

  addClaim(claim: IClaim): Observable<void> {
    return this.ctrlHttp.post(`/claims/`, claim);
  }

  getEmergencyNumber(): Observable<ICtrlResponse<Array<IEmergencyNumber>>> {
    const cached = this.cacheService.get(this.CACHE_KEYS.EMERGENCY_NUMBERS);
    if (cached) return of(cached);

    return this.ctrlHttp.get(`/emergency-numbers/`).pipe(tap((response) => this.cacheService.set(this.CACHE_KEYS.EMERGENCY_NUMBERS, response)));
  }

  getDealerships(
    b2b_client: HttpParams | { [param: string]: string | number | boolean | readonly (string | number | boolean)[] }
  ): Observable<ICtrlResponse<Array<IDealerships>>> {
    const paramsString = JSON.stringify(b2b_client);
    const cached = this.cacheService.get(this.CACHE_KEYS.DEALERSHIPS(paramsString));
    if (cached) return of(cached);

    return this.ctrlHttp
      .get(`/dealership-numbers/`, b2b_client)
      .pipe(tap((response) => this.cacheService.set(this.CACHE_KEYS.DEALERSHIPS(paramsString), response)));
  }

  getInsuredAsset(assetType: AssetType, id: number): Observable<ICtrlResponse<Array<IInsuredAsset>>> {
    const cached = this.cacheService.get(this.CACHE_KEYS.INSURED_ASSET(assetType, id));
    if (cached) return of(cached);

    return this.ctrlHttp
      .get(`/insured-assets/${assetType}`, { id })
      .pipe(tap((response) => this.cacheService.set(this.CACHE_KEYS.INSURED_ASSET(assetType, id), response)));
  }

  async getInsuredItems<T>(activeSchedules: Array<ISchedule>, insuranceType: AssetType): Promise<Array<T>> {
    let returnResult: Array<T> = [];

    for (const schedule of activeSchedules) {
      const scheduleInsuredAssets = (await this.getInsuredAsset(insuranceType, schedule.id).toPromise()) as Array<T>;
      returnResult = returnResult.concat(scheduleInsuredAssets);
    }

    return returnResult;
  }

  getPolicyBenefit(asset_type: AssetType, cover_type = CoverType.Comprehensive, insurer_id: string): Observable<ICtrlResponse<IPolicyBenefit>> {
    return this.ctrlHttp.get(`/policy-documents/`, { asset_type, cover_type, insurer_id });
  }

  getVapsPolicyBenefit(insurer_id: string, cover_type: CoverType, product_id: any): Observable<ICtrlResponse<IPolicyBenefit>> {
    const cached = this.cacheService.get(this.CACHE_KEYS.VAPS_POLICY_BENEFIT(insurer_id, cover_type, product_id));
    if (cached) return of(cached);

    return this.ctrlHttp
      .get(`/policy-documents/`, { cover_type, product_id, insurer_id })
      .pipe(tap((response) => this.cacheService.set(this.CACHE_KEYS.VAPS_POLICY_BENEFIT(insurer_id, cover_type, product_id), response)));
  }

  getAllPolicyBenefits(cover_type = CoverType.Comprehensive): Observable<ICtrlResponse<Array<IPolicyBenefit>>> {
    const cached = this.cacheService.get(this.CACHE_KEYS.ALL_POLICY_BENEFITS(cover_type));
    if (cached) return of(cached);

    return this.ctrlHttp
      .get(`/policy-documents/`, { cover_type })
      .pipe(tap((response) => this.cacheService.set(this.CACHE_KEYS.ALL_POLICY_BENEFITS(cover_type), response)));
  }

  getT2wof(): Observable<ICtrlResponse<Array<IT2wof>>> {
    const cached = this.cacheService.get(this.CACHE_KEYS.T2WOF);
    if (cached) return of(cached);

    return this.ctrlHttp.get(`/t2wof/`, {}).pipe(tap((response) => this.cacheService.set(this.CACHE_KEYS.T2WOF, response)));
  }

  getInsurers(): Observable<ICtrlResponse<Array<IInsurersDetails>>> {
    const cached = this.cacheService.get(this.CACHE_KEYS.INSURERS);
    if (cached) return of(cached);

    return this.ctrlHttp.get(`/insurers/`, {}).pipe(tap((response) => this.cacheService.set(this.CACHE_KEYS.INSURERS, response)));
  }

  getClaimNumbers(): Observable<ICtrlResponse<Array<IClaimNumber>>> {
    const cached = this.cacheService.get(this.CACHE_KEYS.CLAIM_NUMBERS);
    if (cached) return of(cached);

    return this.ctrlHttp.get(`/claim-numbers/`, {}).pipe(tap((response) => this.cacheService.set(this.CACHE_KEYS.CLAIM_NUMBERS, response)));
  }

  getClaimCategories(): Observable<ICtrlResponse<Array<IClaimCategories>>> {
    const cached = this.cacheService.get(this.CACHE_KEYS.CLAIM_CATEGORIES);
    if (cached) return of(cached);

    return this.ctrlHttp.get(`/claim-categories/`, {}).pipe(tap((response) => this.cacheService.set(this.CACHE_KEYS.CLAIM_CATEGORIES, response)));
  }

  cancelAssetFromPolicy(cancelPolicyAsset: ICancelPolicyAsset): Observable<ICtrlResponse<unknown>> {
    return this.ctrlApiHttpService.post(`/policies/cancel/asset/`, cancelPolicyAsset);
  }

  clearPolicyCache(): void {
    Object.values(this.CACHE_KEYS).forEach((key) => {
      if (typeof key === 'string') {
        this.cacheService.clear(key);
      }
    });
  }
}
